/*
 * Role- and capability-aware actions (management audit P1-2 and P0-5). The API stays the only authority: this decides
 * what the console OFFERS. Roles come from `GET /me` (`scopes`, derived from the org role, and `projects[].role`):
 * what a guest or client can never do is hidden; what a staff role lacks is disabled with its reason. Capabilities
 * come from `me.features` (api/openapi.yaml `MeFeatures`); an API that does not send the object is "unknown", and the
 * console keeps its default behavior.
 */
import { SCOPES_BY_ORG_ROLE, type Me, type MeFeatures, type Scope } from '@hangar/shared';
import { useMe } from '@/api/hooks';

export interface Can {
  allowed: boolean;
  /** Never offered to this role (guests, clients): render nothing. */
  hidden: boolean;
  /** Why a visible action is disabled, for its tooltip and accessible description. */
  reason?: string;
}

const ALLOWED: Can = { allowed: true, hidden: false };
const MEMBER_SCOPES: ReadonlySet<Scope> = new Set(SCOPES_BY_ORG_ROLE.member);

const HIDDEN: Can = { allowed: false, hidden: true };

/** The role the person holds on one project (`me.projects`), if any. */
const projectRoleOf = (me: Me, projectId: string | undefined) => (projectId ? me.projects.find((p) => p.projectId === projectId)?.role : undefined);

/** Guests, externals and the project's clients never see what they lack. */
const isExternal = (me: Me, projectRole: string | undefined) => me.orgRole === 'guest' || me.user?.isExternal === true || projectRole === 'client';

/** Pure decision behind useCan. `me` undefined (loading, failed) allows: the API answers 403 if it must. */
export function canFrom(me: Me | undefined, scope: Scope, projectId?: string): Can {
  if (!me) return ALLOWED;
  const projectRole = projectRoleOf(me, projectId);
  if (me.scopes.includes(scope)) {
    // A guest sees only the projects listed in `me.projects`.
    return projectId && me.orgRole === 'guest' && !projectRole ? HIDDEN : ALLOWED;
  }
  if (isExternal(me, projectRole)) return HIDDEN;
  return { allowed: false, hidden: false, reason: MEMBER_SCOPES.has(scope) ? 'Requires member access' : 'Requires admin' };
}

export function useCan(scope: Scope, projectId?: string): Can {
  return canFrom(useMe().data, scope, projectId);
}

/** A capability flag of `GET /me` (`runBuild`, `retry`, `configSync`, `bitrise`, `runner`, `credentials`, `apiTokens`…). */
export type Feature = keyof MeFeatures;

/** `true`/`false` when the API reports its capabilities, `undefined` when it does not (unknown: keep the default). */
export function featureFrom(me: Me | undefined, feature: Feature): boolean | undefined {
  return me?.features ? me.features[feature] : undefined;
}

export function useFeature(feature: Feature): boolean | undefined {
  return featureFrom(useMe().data, feature);
}

/**
 * Environments and usage limits (`POST/PATCH` environments, `PATCH /projects/{id}` `usageLimits`): `projects:write` plus
 * `admin` or the project's `maintainer` role (apps/api/src/projects/project-access.ts). Guests and clients never see
 * these controls; other roles see them disabled with the reason.
 */
export function canConfigureFrom(me: Me | undefined, projectId: string | undefined): Can {
  const write = canFrom(me, 'projects:write', projectId);
  if (!me || !write.allowed) return write;
  if (me.scopes.includes('admin')) return ALLOWED;
  return projectRoleOf(me, projectId) === 'maintainer' ? ALLOWED : { allowed: false, hidden: false, reason: 'Requires admin or the maintainer role on this project' };
}

export function useCanConfigure(projectId: string | undefined): Can {
  return canConfigureFrom(useMe().data, projectId);
}

export const RUN_BUILD_UNAVAILABLE = 'Run build from the console is not available on this Hangar yet: start builds with hangar build';
export const RETRY_UNAVAILABLE = 'Retry is not available on this Hangar yet: start a new build with hangar build';

/**
 * The console's Run build entry points (top bar, phone FAB, Builds empty state): `builds:write` on the project and the
 * API's `runBuild` capability. Hidden for guests and clients; otherwise disabled with the reason.
 */
export function useRunBuildGate(projectId: string | undefined): { can: Can; blockedReason?: string } {
  const can = useCan('builds:write', projectId);
  const runBuild = useFeature('runBuild');
  return { can, blockedReason: runBuild === false ? RUN_BUILD_UNAVAILABLE : undefined };
}
